import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CampusSelect } from '../../common/input/campus';
import { LoadingBar } from '../../common/loading';
import { useApiWithAuth } from '@/hooks/api';
import {
  archiveGroup,
  createGroup,
  loadGroups,
  updateGroup,
} from '@/store/groups/actions';
import type { Pagination } from '../../common/table';
import { DataTable } from '../../common/table';
import type { Group, GroupTypeKey } from '@/shared/types/group';
import { useDisclosure } from '@/components/ui';
import { LayGroupModal } from './modal';
import { getGroupAlias, getGroupProperty, makeGroup } from './utils';
import type { ActionKey } from './columns';
import { makeColumns } from './columns';
import { CreateDropdown } from './create';
import { GroupSelect, TypeSelect } from '@/components/common/input/group';
import { useDefaultCampus } from '@/store/org/hooks';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useUsersStore } from '@/store/users';
import { loadUsers } from '@/store/users/actions';
import { MinistryTypeSelect } from '@/components/common/input/ministrytype';
import type { MinistryTypeKey } from '@/shared/types/ministry';
import { Debug } from '@/components/common/output/debug';
import { useGroupsStore } from '@/store/groups';

export function Structure() {
  const groups = useGroupsStore();
  const users = useUsersStore();
  const { api, auth } = useApiWithAuth();
  const defaultCampus = useDefaultCampus();
  const [campus, setCampus] = useState(defaultCampus);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const lastCampus = useRef<string>('');
  const [editing, setEditing] = useState<{
    mode: 'create' | 'update';
    item: Group | null;
  }>({ mode: 'create', item: null });
  const [ministry, setMinistry] = useState<'' | MinistryTypeKey>('');
  const [type, setType] = useState<'' | GroupTypeKey>('');
  const [parent, setParent] = useState('');
  const [saving, setSaving] = useState(false);

  const pagination: Pagination = {
    enabled: true,
    absolute: true,
    itemsPerPage: 10,
    page,
    onPageChange: (page: number) => setPage(page),
  };

  const onCampusChange = (id: string) => {
    setCampus(id);
  };

  const onMinistryTypeChange = (type: '' | MinistryTypeKey) => {
    setMinistry(type);
  };

  const onTypeChange = (type: '' | GroupTypeKey) => {
    setType(type);
  };

  useEffect(() => {
    loadUsers(users, { force: false, api, auth });
  }, [users, api, auth]);

  useEffect(() => {
    const force = lastCampus.current !== campus;
    if (!force) {
      return;
    }

    lastCampus.current = campus;
    setLoading(true);
    loadGroups(groups, {
      force,
      api,
      auth,
      campus: campus ? campus : undefined,
    })
      .then(() => setLoading(false))
      .catch((e) => console.error(e));
  }, [api, auth, campus, groups]);

  const items = useMemo(() => {
    let list = groups.computed.activeGroupsList;

    if (ministry === '') {
      return [];
    }

    list = list.filter((item) => item.data.ministry === ministry);

    if (type === '') {
      return list;
    }

    list = list.filter((item) => item.data.type === type);

    if (type === 'laygroup2' && parent) {
      list = list.filter((item) => item.data.parent_id === parent);
    }

    return list;
  }, [groups.computed.activeGroupsList, ministry, type, parent]);

  useEffect(() => setPage(1), [items]);

  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onOpenChange: onOpenChangeModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const startCreate = (type: GroupTypeKey, ministry: MinistryTypeKey) => {
    const item = makeGroup({
      campusId: campus,
      parentId: type === 'laygroup2' ? parent || '' : '',
      type,
      ministry,
    });
    setEditing({ item, mode: 'create' });
    onOpenModal();
  };

  const onCreate = async (item: Group) => {
    setSaving(true);
    await createGroup(groups, { api, auth, item });
    setSaving(false);
    onCloseModal();
    clearEditing();
  };

  const onUpdate = async (item: Group) => {
    setSaving(true);
    await updateGroup(groups, { api, auth, item });
    setSaving(false);
    onCloseModal();
    clearEditing();
  };

  const clearEditing = () => {
    setEditing({ item: null, mode: 'create' });
  };

  const onAction = useCallback(
    (item: Group, action: ActionKey) => {
      if (action === 'edit') {
        setEditing({ item, mode: 'update' });
        onOpenModal();
      } else if (action === 'archive') {
        archiveGroup(groups, { api, auth, item });
      }
    },
    [onOpenModal, api, auth, groups],
  );

  const columns = useMemo(() => {
    let columns = makeColumns({ type, onAction });
    if (type === 'laygroup1') {
      columns = columns.filter((col) => col.key !== 'parent');
    }

    if (campus) {
      columns = columns.filter((col) => col.key !== 'campus');
    }

    if (type) {
      columns = columns.filter((col) => col.key !== 'type');
    }

    return columns;
  }, [onAction, type, campus]);

  const emptyDescription = useMemo(() => {
    if (items.length) {
      return '';
    }

    if (ministry === '') {
      return 'Select Ministry';
    }

    if (type === '') {
      return 'Nothing to see here';
    }

    return `No ${getGroupAlias(type as GroupTypeKey, 'type', true)}`;
  }, [items, type, ministry]);

  const typeLaygroup1 = getGroupAlias('laygroup1', 'type');

  return (
    <div>
      <div className='flex flex-row items-center space-x-2'>
        <CampusSelect
          placeholder='Any Campus'
          value={campus}
          onChangeSingle={onCampusChange}
          className='basis-3/12'
          startContent={<Icon icon='mdi:location' width={20} />}
        />
        {campus.length > 0 && (
          <MinistryTypeSelect
            placeholder='Select Ministry'
            value={ministry}
            onChange={onMinistryTypeChange}
            className='basis-3/12'
          />
        )}
        {campus.length > 0 && ministry && (
          <TypeSelect
            ministry={ministry}
            onChange={onTypeChange}
            className='basis-3/12'
            icons
            placeholder='Any Type'
            plural
          />
        )}
        {campus.length > 0 && type === 'laygroup2' && (
          <div className='basis-1/4'>
            <GroupSelect
              onChangeSingle={setParent}
              type={'laygroup1'}
              startContent={
                <Icon icon={getGroupProperty('laygroup1', 'icon')} />
              }
              placeholder={`Any ${typeLaygroup1}`}
            />
          </div>
        )}
        <div className='grow' />
        <CreateDropdown
          onClick={startCreate}
          ministry={ministry}
          triggerClassName='shrink-0'
        />
        <LayGroupModal
          isOpen={isOpenModal}
          onOpenChange={onOpenChangeModal}
          target={editing.item}
          mode={editing.mode}
          onCreate={onCreate}
          onUpdate={onUpdate}
          saving={saving}
        />
      </div>
      <LoadingBar loading={loading} />
      <DataTable
        aria-label='Structure'
        columns={columns}
        items={items}
        loading={loading}
        pagination={pagination}
        empty={{
          icon: 'mdi:account-group',
          description: emptyDescription,
        }}
      />
      <Debug value={{ campus, ministry, type, parent }} />
    </div>
  );
}
